/** Geometry in EXIF-oriented, then clockwise-rotated source pixels. No I/O. */
export type CropPose={x:number;y:number;zoom:number};
export type Point={x:number;y:number};
export type CropGeometry={sw:number;sh:number;w:number;h:number};
export type Viewport={width:number;height:number};
const bounded=(n:number,lo:number,hi:number)=>Math.max(lo,Math.min(hi,n));
export function cropBox(sw:number,sh:number,w:number,h:number,x:number,y:number,zoom=1){
 const scale=Math.min(sw/w,sh/h)/zoom,cw=w*scale,ch=h*scale;
 const left=bounded(x*sw-cw/2,0,sw-cw),top=bounded(y*sh-ch/2,0,sh-ch);
 return [left,top,left+cw,top+ch];
}
export function normalizePose(g:CropGeometry,p:CropPose):CropPose{
 const zoom=bounded(p.zoom,1,8),[l,t,r,b]=cropBox(g.sw,g.sh,g.w,g.h,p.x,p.y,zoom);
 return {x:(l+r)/2/g.sw,y:(t+b)/2/g.sh,zoom};
}
export function panCrop(g:CropGeometry,v:Viewport,p:CropPose,dx:number,dy:number):CropPose{
 const base=normalizePose(g,p),[l,t,r,b]=cropBox(g.sw,g.sh,g.w,g.h,base.x,base.y,base.zoom);
 return normalizePose(g,{x:base.x-dx/v.width*(r-l)/g.sw,y:base.y-dy/v.height*(b-t)/g.sh,zoom:base.zoom});
}
export function zoomCrop(g:CropGeometry,v:Viewport,p:CropPose,zoom:number,from:Point,to:Point=from):CropPose{
 const base=normalizePose(g,p),[l,t,r,b]=cropBox(g.sw,g.sh,g.w,g.h,base.x,base.y,base.zoom);
 const z=bounded(zoom,1,8),scale=Math.min(g.sw/g.w,g.sh/g.h)/z,cw=g.w*scale,ch=g.h*scale;
 const anchorX=l+from.x/v.width*(r-l),anchorY=t+from.y/v.height*(b-t);
 return normalizePose(g,{x:(anchorX-to.x/v.width*cw+cw/2)/g.sw,y:(anchorY-to.y/v.height*ch+ch/2)/g.sh,zoom:z});
}
const midpoint=(a:Point,b:Point)=>({x:(a.x+b.x)/2,y:(a.y+b.y)/2});
const distance=(a:Point,b:Point)=>Math.hypot(a.x-b.x,a.y-b.y);
/** Rebase at every pointer-count transition; cancelling never changes the pose. */
export class CropGesture{
 private points=new Map<number,Point>();
 private basis:{pose:CropPose;points:Point[]}|null=null;
 constructor(private geometry:CropGeometry,private viewport:Viewport){}
 get count(){return this.points.size}
 get pointerIds(){return [...this.points.keys()]}
 private rebase(pose:CropPose){this.basis={pose:normalizePose(this.geometry,pose),points:[...this.points.values()].slice(0,2).map(p=>({...p}))}}
 down(id:number,point:Point,pose:CropPose){this.points.set(id,point);this.rebase(pose)}
 move(id:number,point:Point,pose:CropPose):CropPose{
  if(!this.points.has(id)||!this.basis)return pose;
  this.points.set(id,point);const current=[...this.points.values()].slice(0,2),start=this.basis.points;
  if(current.length===1){
   const next=panCrop(this.geometry,this.viewport,this.basis.pose,current[0].x-start[0].x,current[0].y-start[0].y);
   this.rebase(next);return next;
  }
  const d=distance(start[0],start[1]);
  if(d<1){this.rebase(pose);return pose}
  const next=zoomCrop(this.geometry,this.viewport,this.basis.pose,this.basis.pose.zoom*distance(current[0],current[1])/d,midpoint(start[0],start[1]),midpoint(current[0],current[1]));
  this.rebase(next);return next;
 }
 up(id:number,pose:CropPose){if(this.points.delete(id))this.rebase(pose)}
 cancel(){this.points.clear();this.basis=null}
}
